import type uPlot from 'uplot';

import type { ChartDataPoint, ChartSeries } from './types';

const toSeconds = (timestampMs: number): number => timestampMs / 1000;

const sortPointsByX = (points: ChartDataPoint[]): ChartDataPoint[] =>
  [...points].sort((a, b) => a.x - b.x);

/**
 * ChartSeries[] → uPlot.AlignedData 변환
 * 모든 시리즈의 x값을 union하고, 빈 값은 null로 채움
 */
export const convertToUPlotData = (
  series: ChartSeries[],
  hiddenSeriesIds?: Set<string>,
): uPlot.AlignedData => {
  if (series.length === 0) {
    return [[]];
  }

  const xValues = Array.from(
    new Set(series.flatMap((item) => item.data.map((point) => toSeconds(point.x)))),
  ).sort((a, b) => a - b);

  const xIndexByValue = new Map<number, number>(xValues.map((x, index) => [x, index]));

  const yColumns = series.map((item) => {
    const column = new Array<number | null>(xValues.length).fill(null);

    if (hiddenSeriesIds?.has(item.id)) {
      return column;
    }

    for (const point of sortPointsByX(item.data)) {
      const seconds = toSeconds(point.x);
      const xIndex = xIndexByValue.get(seconds);

      if (xIndex === undefined) {
        continue;
      }

      column[xIndex] = point.y;
    }

    return column;
  });

  return [xValues, ...yColumns] as uPlot.AlignedData;
};

/**
 * 시리즈 구조 변경 감지용 키 생성 — ID 기반
 */
export const buildSeriesStructureKey = (series: ChartSeries[]): string =>
  series.map((item) => item.id).join('|');
